/**
 * A lateral do Desk Cloud (docs/spec/desk-cloud.md, "A página"): as visões, as mesas (pastas
 * inteligentes: o que cada uma pode abrir), a árvore de pastas e o espaço por tipo. Cada item aceita o
 * botão direito, e as pastas, as mesas e a lixeira aceitam arquivos arrastados da lista.
 */
import { useState, type DragEvent, type ReactNode } from 'react';
import { formatBytes } from '../bytes.js';
import { useT } from '../i18n/index.js';
import { usePlanGrid, usePlanName } from '../plans/usePlanName.js';
import { PLAN_IDS, STORAGE_BUCKETS, type DeskFolder, type DeskRoom, type StorageBucket } from '../shared.js';

const NO_FOLDERS: DeskFolder[] = [];
import { useLoggedNav } from '../states/nav.js';
import { useApp } from '../store.js';
import { Icon, type IconName } from '../ui/icons.js';
import { useLongPress } from '../ui/touch.js';
import { emptyTrash, moveFiles, moveFolder, setRooms, trashFiles, downloadZip } from './cloudActions.js';
import { roomMenu } from './cloudMenus.js';
import { KIND_COLORS, childFolders, roomColor, roomInitials, sameLocation, type CloudLocation } from './cloudModel.js';
import { useCloud } from './cloudStore.js';
import { FOLDER_DRAG, ITEMS_DRAG, useCloudUi } from './cloudUi.js';
import { toast } from '../ui/Toaster.js';

/** O que se solta num item da lateral: os arquivos (ids) ou uma pasta. */
type Dropped = { ids: string[] } | { folderId: string };

function dropped(e: DragEvent): Dropped | null {
  try {
    const items = e.dataTransfer.getData(ITEMS_DRAG);
    if (items) return { ids: JSON.parse(items) as string[] };
    const folderId = e.dataTransfer.getData(FOLDER_DRAG);
    if (folderId) return { folderId };
  } catch {
    // arraste torto: nada
  }
  return null;
}

const accepts = (e: DragEvent, folders: boolean) => e.dataTransfer.types.includes(ITEMS_DRAG) || (folders && e.dataTransfer.types.includes(FOLDER_DRAG));

/** Um botão da lateral que também recebe o que se arrasta (`onDropItems`). */
function NavButton({
  at,
  icon,
  lead,
  label,
  count,
  onDropItems,
  acceptFolders = false,
  menu,
  depth = 0,
  toggle,
}: {
  at: CloudLocation;
  icon?: IconName;
  lead?: ReactNode;
  label: string;
  count?: number;
  onDropItems?: (what: Dropped) => void;
  acceptFolders?: boolean;
  menu?: (anchor: { x: number; y: number }) => void;
  depth?: number;
  toggle?: ReactNode;
}) {
  const current = useCloud((s) => s.at);
  const press = useLongPress();
  const [over, setOver] = useState(false);
  const active = sameLocation(current, at);
  return (
    <div className={`rd-cloudNav__row${over ? ' is-over' : ''}`} style={{ '--depth': depth } as React.CSSProperties}>
      {toggle}
      <button
        type="button"
        className={`rd-navItem rd-cloudNav__item${active ? ' is-on' : ''}`}
        aria-current={active ? 'page' : undefined}
        onClick={() => useCloud.getState().go(at)}
        {...(menu ? press.bind(menu) : {})}
        onDragOver={(e) => {
          if (!onDropItems || !accepts(e, acceptFolders)) return;
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          setOver(false);
          const what = onDropItems ? dropped(e) : null;
          if (!what) return;
          e.preventDefault();
          e.stopPropagation();
          onDropItems!(what);
        }}
      >
        {lead ?? (icon ? <Icon name={icon} size={17} /> : null)}
        <span className="rd-cloudNav__label">{label}</span>
        {count !== undefined ? <span className="rd-cloudNav__count">{count}</span> : null}
      </button>
    </div>
  );
}

export function CloudSidebar() {
  const t = useT();
  const ui = useCloudUi();
  const summary = useCloud((s) => s.summary);
  const folders = summary?.folders ?? NO_FOLDERS;
  const views = summary?.views;
  const open = useCloud((s) => s.expanded);

  const filesOf = (ids: string[]) => useCloud.getState().filesOf(ids);
  const dropOnFolder = (folderId: string | null) => (what: Dropped) => {
    if ('ids' in what) void moveFiles(filesOf(what.ids), folderId);
    else {
      const folder = folders.find((f) => f.id === what.folderId);
      if (folder && folder.id !== folderId) void moveFolder(folder, folderId);
    }
  };
  const dropOnRoom = (room: DeskRoom) => (what: Dropped) => {
    if (!('ids' in what)) return;
    void setRooms(filesOf(what.ids), [room.id], []).then((ok) => ok && toast(t('cloud.share.saved'), 'success'));
  };

  const tree = (parentId: string | null, depth: number): ReactNode =>
    childFolders(folders, parentId).map((folder) => {
      const kids = folders.some((f) => f.parentId === folder.id);
      const expanded = open.has(folder.id);
      return (
        <div key={folder.id} role="group">
          <NavButton
            at={{ view: 'folder', folderId: folder.id }}
            icon="folder"
            label={folder.name}
            count={folder.files}
            depth={depth}
            acceptFolders
            onDropItems={dropOnFolder(folder.id)}
            menu={(anchor) => ui.menuForFolder(folder, anchor)}
            toggle={
              kids ? (
                <button
                  type="button"
                  className={`rd-cloudNav__toggle${expanded ? ' is-open' : ''}`}
                  aria-label={t('cloud.folder.inside', { name: folder.name })}
                  aria-expanded={expanded}
                  onClick={() => useCloud.getState().toggleFolder(folder.id)}
                >
                  <Icon name="chevronRight" size={14} />
                </button>
              ) : (
                <span className="rd-cloudNav__toggle" aria-hidden="true" />
              )
            }
          />
          {kids && expanded ? tree(folder.id, depth + 1) : null}
        </div>
      );
    });

  return (
    <aside className="rd-cloudNav" aria-label={t('cloud.title')}>
      <div className="rd-cloudNav__brand">
        <span className="rd-cloudNav__logo" aria-hidden="true">
          <Icon name="cloud" size={18} />
        </span>
        <span>{t('cloud.title')}</span>
      </div>
      <nav className="rd-cloudNav__group">
        <NavButton at={{ view: 'all' }} icon="grid" label={t('cloud.view.all')} count={views?.all} />
        <NavButton at={{ view: 'recent' }} icon="clock" label={t('cloud.view.recent')} count={views?.recent} />
        <NavButton at={{ view: 'favorites' }} icon="star" label={t('cloud.view.favorites')} count={views?.favorites} />
        <NavButton
          at={{ view: 'trash' }}
          icon="trash"
          label={t('cloud.view.trash')}
          count={views?.trash}
          onDropItems={(what) => 'ids' in what && void trashFiles(filesOf(what.ids))}
          menu={(anchor) => ui.openMenu(anchor, [{ label: t('cloud.menu.emptyTrash'), danger: true, disabled: !views?.trash, onSelect: () => void emptyTrash() }], t('cloud.view.trash'))}
        />
      </nav>
      <div className="rd-cloudNav__group">
        <h2 className="rd-navGroupLabel rd-cloudNav__heading">{t('cloud.section.rooms')}</h2>
        {summary?.rooms.map((room) => (
          <NavButton
            key={room.id}
            at={{ view: 'room', roomId: room.id }}
            lead={
              <span className="rd-cloudNav__room" style={{ background: roomColor(room.id) }} aria-hidden="true">
                {roomInitials(room.name)}
              </span>
            }
            label={room.name}
            count={room.files}
            {...(room.gm ? { onDropItems: dropOnRoom(room) } : {})}
            menu={(anchor) =>
              ui.openMenu(
                anchor,
                roomMenu(ui.menuContext, room, {
                  openRoom: (r) => openRoom(r.id),
                  uploadToRoom: (r) => ui.upload({ roomId: r.id }),
                  zipRoom: (r) => void downloadZip({ roomId: r.id }),
                }),
                room.name,
              )
            }
          />
        ))}
        <NavButton
          at={{ view: 'unlinked' }}
          lead={
            <span className="rd-cloudNav__room is-mine" aria-hidden="true">
              <Icon name="user" size={13} />
            </span>
          }
          label={t('cloud.view.unlinked')}
          count={views?.unlinked}
        />
      </div>
      <div className="rd-cloudNav__group">
        <div
          className="rd-cloudNav__headingRow"
          onDragOver={(e) => {
            if (accepts(e, true)) e.preventDefault();
          }}
          onDrop={(e) => {
            const what = dropped(e);
            if (what) dropOnFolder(null)(what);
          }}
        >
          <h2 className="rd-navGroupLabel rd-cloudNav__heading">{t('cloud.section.folders')}</h2>
          <button type="button" className="rd-iconBtn rd-cloudNav__add" aria-label={t('cloud.newFolder')} title={t('cloud.newFolder')} onClick={() => ui.folderActions.newFolder(null)}>
            <Icon name="plus" size={16} />
          </button>
        </div>
        <div role="tree" aria-label={t('cloud.section.folders')}>
          {tree(null, 0)}
        </div>
      </div>
      <StorageBlock />
    </aside>
  );
}

/** Abre a mesa: a que já está aberta só volta para a tela. */
export function openRoom(roomId: string): void {
  const app = useApp.getState();
  if (app.activeRoomId === roomId) useLoggedNav.getState().go({ kind: 'room', roomId });
  else app.joinRoom(roomId);
}

/** O espaço: o plano, a barra por tipo, o maior arquivo e "Ver planos · até Y" (do catálogo). */
export function StorageBlock({ compact = false }: { compact?: boolean }) {
  const t = useT();
  const summary = useCloud((s) => s.summary);
  const plan = useApp((s) => s.plan);
  const planName = usePlanName();
  const grid = usePlanGrid();
  const go = useLoggedNav((s) => s.go);
  if (!summary) return null;
  // a cota vem do plano de agora: muda na hora com o aviso do servidor, sem reler o resumo
  const { used, trash, byKind } = summary.usage;
  const quota = plan?.entitlements.storageBytes ?? summary.usage.quota;
  const full = used >= quota;
  const biggest = Math.max(...PLAN_IDS.map((id) => grid[id].storageBytes));
  const segments: { bucket: StorageBucket | 'trash'; bytes: number }[] = [...STORAGE_BUCKETS.map((bucket) => ({ bucket, bytes: byKind[bucket] ?? 0 })), { bucket: 'trash' as const, bytes: trash }].filter((segment) => segment.bytes > 0);
  const label = (bucket: StorageBucket | 'trash') => t(`cloud.kinds.${bucket}`);
  return (
    <section className={`rd-cloudStorage${full ? ' is-full' : ''}${compact ? ' is-compact' : ''}`} aria-label={t('cloud.section.storage')}>
      <div className="rd-cloudStorage__head">{t('cloud.section.storage')}</div>
      {/* o espaço e o plano: clicar abre Configurações → Plano (docs/spec/planos.md) */}
      <button type="button" className="rd-cloudStorage__used" title={t('cloud.storage.open')} onClick={() => go({ kind: 'settings', page: 'plan' })}>
        {t('cloud.storage.of', { used: formatBytes(used), quota: formatBytes(quota) })}
        {plan ? ` · ${planName(plan.id)}` : ''}
      </button>
      <div className="rd-cloudStorage__bar" role="img" aria-label={t('cloud.storage.aria', { used: formatBytes(used), quota: formatBytes(quota) })}>
        {segments.map((segment) => (
          <span key={segment.bucket} style={{ width: `${Math.min(100, (segment.bytes / Math.max(1, quota)) * 100)}%`, background: KIND_COLORS[segment.bucket] }} />
        ))}
      </div>
      {full ? <p className="rd-cloudStorage__full">{t('cloud.storage.full')}</p> : null}
      {compact ? null : (
        <ul className="rd-cloudStorage__legend">
          {segments.map((segment) => (
            <li key={segment.bucket}>
              <span className="rd-cloudStorage__dot" style={{ background: KIND_COLORS[segment.bucket] }} aria-hidden="true" />
              <span className="rd-cloudStorage__legendText">
                {label(segment.bucket)} · {formatBytes(segment.bytes)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {plan ? <div className="rd-cloudStorage__max">{t('cloud.storage.maxFile', { size: formatBytes(plan.entitlements.maxFileBytes) })}</div> : null}
      <button type="button" className="rd-cloudStorage__plans" onClick={() => go({ kind: 'settings', page: 'plan' })}>
        {biggest > quota ? t('cloud.storage.plans', { size: formatBytes(biggest) }) : t('cloud.storage.plansOnly')}
      </button>
    </section>
  );
}

/** Os blocos de pasta no topo da lista (as filhas da pasta aberta, ou as da raiz). */
export function folderTiles(folders: readonly DeskFolder[], at: CloudLocation): DeskFolder[] {
  if (at.view === 'folder') return childFolders(folders, at.folderId);
  if (at.view === 'all') return childFolders(folders, null);
  return [];
}
